import { useEffect, useMemo, Children, isValidElement } from 'react'
import { v4 as uuidv4 } from 'uuid';
import { useState, useRef } from 'react';
import useCoarsePointer from '../../service/useCoarsePointer';
import './spinner.css'

export default function Select({ name, id, value = "", onChange, label, className = '', size = 4, options, children, tooltip, placeholder = "בחר", required = false, style, errorText, locked = false,loading, ...other }) {
    //local State Managment
    const [droppedDown, SetDroppedDown] = useState(false) //DropDown state
    const [selectLabel, setSelectLabel] = useState("") //Selected element Label
    const [dropSize, setDropSize] = useState(size)
    const [validationMessage, setValidationMessage] = useState("")
    const isCoarse = useCoarsePointer() // touch / mobile devices -> native select


    useEffect(() => {
        if (children?.length - 1 < size)
            setDropSize(children.length)
        else if (options?.length < size)
            setDropSize(options.length)
        else
            setDropSize(size)
    }, [children, options, size])

    // Select HTML Ref Control
    const selectRef = useRef(null);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null); // used for click-outside on the touch dropdown

    const onInvalid = (e) => {
        setValidationMessage("שדה חובה.")
    }

    // Handle update value via value prop
    useEffect(() => {
        if (value !== undefined && value !== '' && value !== null) {
            if (options?.find(item => item?.value === String(value))?.label)
                setSelectLabel(options?.find(item => item?.value === String(value))?.label);
            else if ([...selectRef?.current?.children]?.find(item => item?.value === String(value))?.label)
                setSelectLabel([...selectRef?.current?.children]?.find(item => item?.value === String(value))?.label);
            else setSelectLabel(value)
        }
        else
            setSelectLabel("");
        // selectRef.current.value = value;
    }, [value, options])

    useEffect(() => {
        if (value || !required)
            setValidationMessage("");
    }, [value, required])

    // Close the touch dropdown when tapping/clicking outside of it
    useEffect(() => {
        if (!isCoarse || !droppedDown) return;
        const handlePointerDown = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target))
                SetDroppedDown(false);
        };
        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [isCoarse, droppedDown])

    // Handle Dynamic element Id
    id = useMemo(() => (id) ? id : uuidv4(), [id]);

    // Handle Update Value 
    const handleChangeValue = (event) => {
        // Update via Prop CallBack Function
        if (typeof (onChange) === 'function')
            onChange(event.target.value);
        // Update ONLY visible label
        else {
            let selectedOption = options?.find(item => item.value === event.target.value);
            setSelectLabel(selectedOption?.label || event.target.value);
        }
        // Close DropDown
        // SetDroppedDown(false);
    }

    // Close DropDown on Blur
    const handleBlur = (event) => {
        if (event.relatedTarget !== inputRef.current) {
            SetDroppedDown(false);
            if (required && !value)
                event.target.checkValidity();
        }
    }

    const handleKeyDown = (e) => {
        if ([13, 32].includes(e.keyCode)) {
            SetDroppedDown(prev => !prev)
        }
        else
            if (e.keyCode === 27)
                SetDroppedDown(false);
    }

    const handleClick = (event) => {
        if (dropSize === 1) handleChangeValue(event);
        SetDroppedDown(false);

    }

    // Touch / mobile branch: a custom div-based dropdown styled like the desktop
    // version (a real <select size> renders the OS picker on phones and can't be
    // styled). A visually-hidden native <select> carries the value/name so form
    // submission (FormData) and required-validation keep working unchanged.
    if (isCoarse) {
        // Flatten prop options + child <option>s into a single list for the custom menu
        const propOptions = (options || []).map(item => {
            if (typeof item === 'number' || typeof item === 'string')
                return { value: item, label: item };
            if (item && typeof item === 'object' && item.hasOwnProperty('value') && item.hasOwnProperty('label'))
                return { value: item.value, label: item.label };
            return null;
        }).filter(Boolean);
        const childOptions = Children.toArray(children)
            .filter(child => isValidElement(child) && child.type === 'option')
            .map(child => ({ value: child.props.value, label: child.props.children, disabled: child.props.disabled, hidden: child.props.hidden }));
        const menuOptions = [...propOptions, ...childOptions].filter(opt => !opt.hidden);

        const handleSelectOption = (optValue) => {
            if (typeof (onChange) === 'function')
                onChange(optValue);
            else {
                const selected = [...propOptions, ...childOptions].find(opt => String(opt.value) === String(optValue));
                setSelectLabel(selected?.label ?? optValue);
            }
            SetDroppedDown(false);
        };

        return (
            <div className={`field-wrapper ${className}`} style={style} ref={wrapperRef}>
                <label htmlFor={id}>{(required) ? <>*&nbsp;{label}</> : <>{label}</>}
                    {(tooltip) && <div role="tooltip" aria-label={name} tool-tip={tooltip}><img src="./icons/question-mark-circle.svg" alt='?' /></div>}
                </label>
                <div className={`input-wrapper ${(droppedDown) ? 'dropped-down' : ''}`}>
                    <input tabIndex={-1} ref={inputRef} value={selectLabel} placeholder={placeholder} readOnly required={required} disabled={locked}
                        className='input-select'
                        autoComplete="off"
                        name='select-input-label'
                        onClick={(locked) ? undefined : () => SetDroppedDown(prev => !prev)}
                    />
                    {loading && <div className="spinner-overlay" />}
                    {/* Hidden native select: keeps value/name for FormData + constraint validation */}
                    <select id={id} name={name} ref={selectRef} {...other} className="visually-hidden-select"
                        value={value ?? ''}
                        required={!value && required}
                        disabled={locked}
                        tabIndex={-1}
                        aria-hidden="true"
                        onChange={handleChangeValue}
                        onInvalid={onInvalid}
                    >
                        {!value && <option value="" disabled hidden>{placeholder}</option>}
                        {(options) && options.map((item, index) => {
                            if (typeof (item) === 'number' || typeof (item) === 'string')
                                return <option key={index} value={item} >{item}</option>
                            else if (typeof (item) === "object" && item.hasOwnProperty('value') && item.hasOwnProperty('label'))
                                return <option key={index} value={item?.value} >{item?.label}</option>
                            return <></>
                        })}
                        {children}
                    </select>
                    {droppedDown && !locked && (
                        <ul className="custom-options" role="listbox">
                            {menuOptions.map((opt, index) => (
                                <li key={index} role="option"
                                    aria-selected={String(opt.value) === String(value)}
                                    aria-disabled={opt.disabled || undefined}
                                    className={`custom-option ${String(opt.value) === String(value) ? 'selected' : ''} ${opt.disabled ? 'disabled' : ''}`}
                                    onClick={opt.disabled ? undefined : () => handleSelectOption(opt.value)}
                                >
                                    {opt.label}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                {!!validationMessage && (
                    <div className='validation-message'>
                        {errorText || validationMessage}
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className={`field-wrapper ${className}`} style={style}>
            <label htmlFor={id}>{(required) ? <>*&nbsp;{label}</> : <>{label}</>}
                {(tooltip) && <div role="tooltip" aria-label={name} tool-tip={tooltip}><img src="./icons/question-mark-circle.svg" alt='?' /></div>}
            </label>
            <div className={`input-wrapper ${(droppedDown) ? 'dropped-down' : ''}`}>
                <input tabIndex={-1} ref={inputRef} value={selectLabel} placeholder={placeholder} readOnly required={required} disabled={locked}
                    className='input-select'
                    autoComplete="off"
                    name='select-input-label'
                    onClick={(locked) ? undefined : (e) => { selectRef.current.focus(); SetDroppedDown(prev => !prev) }}
                />
                {loading &&<div className="spinner-overlay"/>}
                <select id={id} name={name} size={dropSize} ref={selectRef} {...other} required={!value && required}
                    onClick={handleClick}
                    onChange={handleChangeValue}
                    onKeyDown={handleKeyDown}
                    tabIndex={(locked) ? -1 : 0}
                    onInvalid={onInvalid}
                    onBlur={handleBlur}
                >
                    {(options) && options.map((item, index) => {
                        if (typeof (item) === 'number' || typeof (item) === 'string')
                            return <option key={index} value={item} >{item}</option>
                        else if (typeof (item) === "object" && item.hasOwnProperty('value') && item.hasOwnProperty('label'))
                            return <option key={index} value={item?.value} >{item?.label}</option>
                        return <></>
                    })}
                    {children}
                </select>
            </div>
            {!!validationMessage && (
                <div className='validation-message'>
                    {errorText || validationMessage}
                </div>
            )}
        </div>
    )
}
